import call from "./call";
import {
  VoicesPost,
  VoicesPostContent,
  VoicesPostOrder,
  VoicesPostPayload,
  VoicesPostReview,
  VoicesPostTarget,
} from "../Models/voicesPosts";
import { saveDownload } from "../Helpers/voicesDownload";
import { voicesHeaders } from "../Helpers/voicesWorkspace";
import { VoicesCalendarFilters, VoicesCalendarPost } from "../Models/voicesCalendar";

const postsUrl = "/v1/voices/posts/";

export const getVoicesPosts = async (
  order: VoicesPostOrder,
  campaignId?: number,
): Promise<VoicesPost[]> => {
  const response = await call.get<{ posts: VoicesPost[] }>(postsUrl, {
    params: { order, ...(campaignId ? { campaign_id: campaignId } : {}) },
    headers: voicesHeaders(),
    snack: { error: "voices.postsFetchError" },
  });
  return response.data.posts;
};

export const createVoicesPost = async (data: VoicesPostPayload): Promise<VoicesPost> => {
  const response = await call.post<VoicesPost>(postsUrl, data, {
    headers: voicesHeaders(),
    snack: { success: "voices.postCreateSuccess", error: "voices.postSaveError" },
  });
  return response.data;
};

export const updateVoicesPost = async (
  postId: number,
  data: VoicesPostContent,
): Promise<VoicesPost> => {
  const response = await call.put<VoicesPost>(`${postsUrl}${postId}/`, data, {
    headers: voicesHeaders(),
    snack: { success: "voices.postUpdateSuccess", error: "voices.postSaveError" },
  });
  return response.data;
};

export const deleteVoicesPost = async (postId: number): Promise<void> => {
  await call.delete(`${postsUrl}${postId}/`, {
    headers: voicesHeaders(),
    snack: { success: "voices.postDeleteSuccess", error: "voices.postDeleteError" },
  });
};

export const draftVoicesPost = async (postId: number): Promise<VoicesPost> => {
  const response = await call.post<VoicesPost>(
    `${postsUrl}${postId}/draft/`,
    {},
    {
      headers: voicesHeaders(),
      snack: { success: "voices.postDraftSuccess", error: "voices.postDraftError" },
    },
  );
  return response.data;
};

const postAction = async (
  postId: number,
  action: "submit" | "retry",
  success: string,
  error: string,
): Promise<VoicesPost> => {
  const response = await call.post<VoicesPost>(
    `${postsUrl}${postId}/${action}/`,
    {},
    { headers: voicesHeaders(), snack: { success, error } },
  );
  return response.data;
};

export const submitVoicesPost = (postId: number) =>
  postAction(postId, "submit", "voices.postSubmitSuccess", "voices.postSubmitError");

export const stopVoicesPost = async (postId: number, note: string): Promise<VoicesPost> => {
  const response = await call.post<VoicesPost>(
    `${postsUrl}${postId}/stop/`,
    note ? { note } : {},
    {
      headers: voicesHeaders(),
      snack: { success: "voices.postStopSuccess", error: "voices.postStopError" },
    },
  );
  return response.data;
};

export const retryVoicesPost = (postId: number) =>
  postAction(postId, "retry", "voices.postRetrySuccess", "voices.postRetryError");

export const approveVoicesPost = async (
  postId: number,
  changes: Partial<VoicesPostContent>,
): Promise<VoicesPost> => {
  const response = await call.post<VoicesPost>(`${postsUrl}${postId}/approve/`, changes, {
    headers: voicesHeaders(),
    snack: { success: "voices.postApproveSuccess", error: "voices.postApproveError" },
  });
  return response.data;
};

export const rejectVoicesPost = async (postId: number, note: string): Promise<VoicesPost> => {
  const response = await call.post<VoicesPost>(
    `${postsUrl}${postId}/reject/`,
    { note },
    {
      headers: voicesHeaders(),
      snack: { success: "voices.postRejectSuccess", error: "voices.postRejectError" },
    },
  );
  return response.data;
};

export const previewVoicesPostChecks = async (
  postId: number,
  content: VoicesPostContent,
): Promise<VoicesPostReview> => {
  const response = await call.post<VoicesPostReview>(`${postsUrl}${postId}/checks/`, content, {
    headers: voicesHeaders(),
  });
  return response.data;
};

export const uploadVoicesPostImage = async (postId: number, file: File): Promise<VoicesPost> => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await call.post<VoicesPost>(`${postsUrl}${postId}/image/`, formData, {
    headers: voicesHeaders(),
    snack: {
      success: "voices.postImageSuccess",
      error: "voices.postImageError",
      415: "voices.postImageType",
      413: "voices.postImageSize",
    },
  });
  return response.data;
};

export const deleteVoicesPostImage = async (postId: number): Promise<VoicesPost> => {
  const response = await call.delete<VoicesPost>(`${postsUrl}${postId}/image/`, {
    headers: voicesHeaders(),
    snack: { error: "voices.postImageDeleteError" },
  });
  return response.data;
};

export const getVoicesPostImage = async (postId: number): Promise<Blob> => {
  const response = await call.get(`${postsUrl}${postId}/image/`, {
    headers: voicesHeaders(),
    responseType: "blob",
  });
  return response.data;
};

export const nextVoicesPostDate = async (
  target: VoicesPostTarget | null,
): Promise<{ scheduled_at: string | null }> => {
  const response = await call.post<{ scheduled_at: string | null }>(
    `${postsUrl}next_date/`,
    { target },
    { headers: voicesHeaders(), snack: { error: "voices.nextDateError" } },
  );
  return response.data;
};

export const editScheduledVoicesPost = async (
  postId: number,
  changes: Partial<VoicesPostContent>,
): Promise<VoicesPost> => {
  const response = await call.put<VoicesPost>(`${postsUrl}${postId}/scheduled/`, changes, {
    headers: voicesHeaders(),
    snack: { success: "voices.postEditScheduledSuccess", error: "voices.postSaveError" },
  });
  return response.data;
};

export const getVoicesPost = async (postId: number): Promise<VoicesPost> => {
  const response = await call.get<VoicesPost>(`${postsUrl}${postId}/`, {
    headers: voicesHeaders(),
    snack: { error: "voices.postsFetchError" },
  });
  return response.data;
};

export const getVoicesCalendar = async (
  from: string,
  to: string,
  filters: VoicesCalendarFilters,
): Promise<VoicesCalendarPost[]> => {
  const response = await call.get<{ posts: VoicesCalendarPost[] }>(`${postsUrl}calendar/`, {
    params: { from, to, ...filters },
    headers: voicesHeaders(),
    snack: { error: "voices.calendarFetchError" },
  });
  return response.data.posts;
};

export const moveVoicesPost = async (postId: number, scheduledAt: string): Promise<VoicesPost> => {
  const response = await call.post<VoicesPost>(
    `${postsUrl}${postId}/move/`,
    { scheduled_at: scheduledAt },
    {
      headers: voicesHeaders(),
      snack: {
        success: "voices.postMoveSuccess",
        error: "voices.postMoveError",
        409: "voices.postMoveWeeklyLimit",
        400: "voices.postMoveInvalid",
      },
    },
  );
  return response.data;
};

export const exportVoicesPostsForBuffer = async (
  target: string | null,
  postIds: number[],
): Promise<void> => {
  const response = await call.get<Blob>(`${postsUrl}export/`, {
    params: {
      ...(target ? { target } : {}),
      ...(postIds.length ? { ids: postIds.join(",") } : {}),
    },
    headers: voicesHeaders(),
    responseType: "blob",
    snack: { error: "voices.exportError", 404: "voices.exportEmpty" },
  });
  saveDownload(response, "buffer-posts.csv");
};
